import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import FormControl from './FormControl';
import Geosuggest from 'react-geosuggest';
import GoogleMapsLoader from 'google-maps';
import { ScrollElementToTarget } from '../../../util/WindowHelper';
import i18n from '../../../i18n/translate';
import { pickDataAttributes } from '../../../Util';

export default class AddressPickerControl extends FormControl {
  static propTypes = {
    addressFieldPrefix: PropTypes.string,
    ariaLabel: PropTypes.string,
    disabled: PropTypes.bool,
    fieldClassName: PropTypes.string,
    formatAddressPropName: PropTypes.func,
    googleMapsApiKey: PropTypes.string.isRequired,
    id: PropTypes.string,
    includeCountries: PropTypes.bool,
    includeStatesAndCities: PropTypes.bool,
    initialData: PropTypes.object,
    inputClassName: PropTypes.string,
    onChange: PropTypes.func,
    onDataLoaded: PropTypes.func,
    onValueChange: PropTypes.func,
    placeholder: PropTypes.string,
    scrollContainerId: PropTypes.string,
    valueOverride: PropTypes.bool
  };

  static defaultProps = Object.assign({}, FormControl.defaultProps, {
    addressFieldPrefix: 'location',
    includeStatesAndCities: false,
    includeCountries: false
  });

  static addressPropVals = [
    '_administrative_area_level_1',
    '_administrative_area_level_2',
    '_country',
    '_latitude',
    '_locality',
    '_longitude',
    '_neighborhood',
    '_postal_code',
    '_route',
    '_street_number'
  ];

  constructor(props) {
    super(props);
    this.onSelect = this.onSelect.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.skipSuggest = this.skipSuggest.bind(this);
    this.validSuggestion = this.validSuggestion.bind(this);
    this.onOpen = this.onOpen.bind(this);

    const { initialData, addressFieldPrefix } = props;

    this.addressProps = AddressPickerControl.addressPropVals.map(prop => {
      return `${addressFieldPrefix}${prop}`;
    });

    let value = '';
    if (initialData) {
      if (initialData.location_formatted_address) {
        value = initialData.location_formatted_address;
      }
      else if (initialData[`${addressFieldPrefix}_formatted`]) {
        value = initialData[`${addressFieldPrefix}_formatted`];
      }
      else if (initialData[`${addressFieldPrefix}_street_number`]) {
        value = `${initialData[`${addressFieldPrefix}_street_number`]} ${initialData[`${addressFieldPrefix}_route`]} ${initialData[`${addressFieldPrefix}_locality`]}, ${initialData[`${addressFieldPrefix}_administrative_area_level_1`]}`;
      }
    }
    this.addressSelected = !!value;
    this.state = { value };
    this.addressProps.forEach((prop) => {
      this.state[prop] = initialData && initialData[prop] ?
        initialData[prop] : '';
    });

    if (initialData) {
      if (typeof props.onDataLoaded === 'function') {
        props.onDataLoaded(this.state.value);
      }
    }
  }

  componentWillReceiveProps(newProps) {
    let { initialData, addressFieldPrefix } = newProps;
    let value = '';
    if (newProps.valueOverride) {
      if (initialData.location_formatted_address && initialData.location_formatted_address !== this.state.value) {
        value = initialData.location_formatted_address;
      }
      else if (initialData[`${addressFieldPrefix}_formatted`] && initialData[`${addressFieldPrefix}_formatted`] !== this.state.value) {
        value = initialData[`${addressFieldPrefix}_formatted`];
      }
      else if (initialData[`${addressFieldPrefix}_street_number`] && initialData[`${addressFieldPrefix}_street_number`] !== this.state.value) {
        value = `${initialData[`${addressFieldPrefix}_street_number`]} ${initialData[`${addressFieldPrefix}_route`]} ${initialData[`${addressFieldPrefix}_locality`]}, ${initialData[`${addressFieldPrefix}_administrative_area_level_1`]}`;
      }
    } else {
      value = this.state.value;
    }
    this.setState({ value });
  }

  componentDidMount() {
    GoogleMapsLoader.KEY = this.props.googleMapsApiKey;
    GoogleMapsLoader.VERSION = '3.34'
    GoogleMapsLoader.LIBRARIES = ['places'];
    GoogleMapsLoader.load((google) => this.setState({ googleMaps: google.maps }));
  }

  setCleanSlateAddress = (address) => {
    Object.keys(this.state)
      .filter(item => item.indexOf(`${this.props.addressFieldPrefix}_`) > -1)
      .forEach(addressKey => { address[addressKey] = '' });
    // manually set address_formatted to empty
    address.address_formatted = '';
  }

  skipSuggest(suggest) {
    return !this.validSuggestion(suggest);
  }

  onSelect(suggest) {
    if (!this.validSuggestion(suggest)) {
      this.addressSelected = false;
      return;
    }
    this.input.setCustomValidity('');
    this.addressSelected = true;
    //extract the data and place it where we can save later
    let address = {};
    // we have to clean state of old key from any previous address
    this.setCleanSlateAddress(address);

    address.value = suggest.label;
    suggest.gmaps.address_components.forEach((component) => {
      component.types.forEach((type) => {
        if (this.addressProps.includes(`${this.props.addressFieldPrefix}_${type}`)) {
          address[`${this.props.addressFieldPrefix}_${type}`] = component.long_name;
        }
      });
    });
    address[`${this.props.addressFieldPrefix}_latitude`] = suggest.gmaps.geometry.location.lat();
    address[`${this.props.addressFieldPrefix}_longitude`] = suggest.gmaps.geometry.location.lng();

    address[`${this.props.addressFieldPrefix}_google_place_id`] = suggest.gmaps.place_id;

    this.setState(address);
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(suggest);
    }
    if (typeof this.props.onValueChange === 'function') {
      this.props.onValueChange(address);
    }

    let valid = this.isValid();
    if (typeof this.props.setError === 'function') {
      this.props.setError(!valid);
    }
  }

  isValid = () => {
    let node = ReactDOM.findDOMNode(this.geo);
    if (node) {
      let input = ReactDOM.findDOMNode(this.geo).querySelector('input');
      return input.validity.valid;
    }
  }

  validSuggestion(suggest) {
    const statesAndCitiesValidTypes = this.props.includeStatesAndCities ? ['administrative_area_level_1', 'locality'] : [];
    const countriesValidTypes = this.props.includeCountries ? ['country'] : [];

    if (suggest) {
      const validTypes = ['street_address', 'route', 'premise', 'subpremise', 'point_of_interest', ...statesAndCitiesValidTypes, ...countriesValidTypes];
      const suggestionTypes = suggest.gmaps ? suggest.gmaps.types : suggest.types;

      return suggestionTypes.some((type) => validTypes.includes(type));
    }
  }

  onBlur(userInput) {
    if (typeof this.props.onBlur === 'function') {
      this.props.onBlur(userInput);
    }
    if (!this.addressSelected) {
      let clearAddress = {};
      this.addressProps.forEach((prop) => (clearAddress[prop] = undefined));
      this.setState(clearAddress);
      let message = userInput ? i18n.t('Please select a valid address') : i18n.t("This field is required");
      this.input.setCustomValidity(message);
      if (typeof this.props.setError === 'function') {
        this.props.setError(true, message);
      }
    }
  }

  checkError() {
    if (typeof this.props.setError === 'function') {
      if (!this.isValid()) {
        this.props.setError(true, i18n.t('Please select a valid address'));
      } else {
        this.props.setError(false);
      }
    }
  }

  onOpen() {
    this.addressSelected = false;
    let el = document.getElementById(this.props.scrollContainerId);
    let target = document.activeElement;
    ScrollElementToTarget(el, target, 155);
  }

  onSuggestNoResults = () => {
    let address = {};
    this.setCleanSlateAddress(address);

    this.setState(address);
  }

  onRef = (geo) => {
    this.geo = geo;
    if (this.geo) {
      this.input = ReactDOM.findDOMNode(this.geo).querySelector('input');
    }
  }

  render() {
    const dataAttributes = pickDataAttributes(this.props);
    const ariaLabel = this.props.ariaLabel || 'Address (Use keyboard for predictive text)';

    return (
      <div>
        {this.state.googleMaps ?
          <Geosuggest
            {...dataAttributes}
            aria-label={ariaLabel}
            aria-required={this.props.required}
            autoActivateFirstSuggest={true}
            disabled={this.props.disabled}
            googleMaps={this.state.googleMaps}
            id={this.props.id}
            initialValue={this.state.value}
            inputClassName="form-control"
            onBlur={this.onBlur}
            onFocus={this.onOpen}
            onSuggestNoResults={this.onSuggestNoResults}
            onSuggestSelect={this.onSelect}
            placeholder={this.props.placeholder}
            ref={this.onRef}
            required={this.props.required}
            skipSuggest={this.skipSuggest}
          />
          : null
        }
        {this.addressProps.map((prop) => <input type="hidden" value={this.state[prop]} key={prop}
          disabled={this.props.disabled}
          name={this.props.formatAddressPropName ? this.props.formatAddressPropName(prop) : prop} />)}
      </div>
    );
  }
}
